export type SceneType = 'SPEAKER_ONLY' | 'SPLIT_SCREEN' | 'CIRCLE_PIP' | 'FULL_BROLL' | 'FULL_BLEED_BROLL' | 'OVERLAY';

export type OverlayTemplate = string;

export interface OverlayLibraryEntry {
  id: string;
  name: string;
  pngUrl: string;
  cutout: { left: number; top: number; width: number; height: number };
  objectPosition: string;
  created?: string;
}

export interface OverlayConfig {
  template: string;
  pngUrl?: string;
  cutout?: { left: number; top: number; width: number; height: number };
  objectPosition?: string;
}

export type BeatType = 
  | 'HOOK' 
  | 'SETUP' 
  | 'REVEAL' 
  | 'ESCALATE' 
  | 'EVIDENCE'
  | 'PATTERN_INTERRUPT' 
  | 'DARK_TURN'
  | 'THESIS' 
  | 'MIC_DROP';

export type BrollSourceType = 'flux' | 'lora' | 'brave' | 'pexels' | 'infographic' | 'component' | 'upload' | 'library' | 'lottie';

export type InfographicDirection = 'bottom' | 'top' | 'left' | 'right';
export type InfographicBgStyle = 'static' | 'bokeh' | 'pulse';

export type ComponentType = 'winner-card' | 'template-dashboard';

export type CaptionStyle = 'teal-pill' | 'word-highlight' | 'minimal' | 'none';
export type CaptionPosition = 'bottom' | 'lower-third' | 'middle';
export type CaptionPositionNum = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9; // 1=top, 5=center, 9=bottom

// PIP position on a 3x3 grid
export type PipPosition = 'top-left' | 'top-center' | 'top-right' | 'middle-left' | 'middle-center' | 'middle-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

export interface BrollConfig {
  prompt: string;
  source: BrollSourceType;
  path?: string;
  url?: string;
  // Infographic options
  infographicDirection?: InfographicDirection;
  infographicBgStyle?: InfographicBgStyle;
  // Component options
  componentType?: ComponentType;
  componentNotes?: string;
  // Media type (image or video — used by renderer to pick Img vs OffthreadVideo)
  mediaType?: 'image' | 'video';
}

export type EffectType = 
  | 'none' 
  | 'slide-up' | 'slide-down' | 'slide-left' | 'slide-right'
  | 'scale-pop' | 'scale-shrink' 
  | 'bounce-drop' | 'bounce-up'
  | 'fade'
  | 'elastic-resize' 
  | 'zoom-burst' | 'zoom-pulse'
  | 'rotate-in' | 'rotate-out'
  | 'swing-in' | 'swing-out'
  | 'diagonal-slide'
  | 'wipe-left' | 'wipe-down'
  | 'flip-in'
  | 'squeeze' | 'stretch'
  | 'shake';

export type SoundType = 'none' | 'swoosh' | 'pop' | 'whoosh' | 'click' | 'rise';

export const EFFECTS: { id: EffectType; name: string; description: string }[] = [
  { id: 'none', name: 'None', description: 'Instant cut' },
  // Slides
  { id: 'slide-up', name: 'Slide Up', description: 'Slides in from bottom' },
  { id: 'slide-down', name: 'Slide Down', description: 'Slides in from top' },
  { id: 'slide-left', name: 'Slide Left', description: 'Slides in from right' },
  { id: 'slide-right', name: 'Slide Right', description: 'Slides in from left' },
  // Scales
  { id: 'scale-pop', name: 'Scale Pop', description: 'Pops in with overshoot' },
  { id: 'scale-shrink', name: 'Scale Shrink', description: 'Shrinks in from large' },
  // Bounces
  { id: 'bounce-drop', name: 'Bounce Drop', description: 'Drops and bounces' },
  { id: 'bounce-up', name: 'Bounce Up', description: 'Bounces up from bottom' },
  // Fades
  { id: 'fade', name: 'Fade In', description: 'Fades in smoothly' },
  // Elastic
  { id: 'elastic-resize', name: 'Elastic', description: 'Elastic spring effect' },
  // Zooms
  { id: 'zoom-burst', name: 'Zoom Burst', description: 'Quick zoom in' },
  { id: 'zoom-pulse', name: 'Zoom Pulse', description: 'Pulsing zoom' },
  // Rotates
  { id: 'rotate-in', name: 'Rotate In', description: 'Rotates into view' },
  { id: 'rotate-out', name: 'Rotate Out', description: 'Rotates from offset' },
  // Swings
  { id: 'swing-in', name: 'Swing In', description: 'Swings in like a door' },
  { id: 'swing-out', name: 'Swing Out', description: 'Swings from opposite side' },
  // Diagonal
  { id: 'diagonal-slide', name: 'Diagonal', description: 'Slides diagonally' },
  // Wipes
  { id: 'wipe-left', name: 'Wipe Left', description: 'Wipes in from right' },
  { id: 'wipe-down', name: 'Wipe Down', description: 'Wipes in from top' },
  // 3D
  { id: 'flip-in', name: 'Flip In', description: '3D flip effect' },
  // Transforms
  { id: 'squeeze', name: 'Squeeze', description: 'Squeezes in horizontally' },
  { id: 'stretch', name: 'Stretch', description: 'Stretches in vertically' },
  // Fun
  { id: 'shake', name: 'Shake', description: 'Shakes into position' },
];

export const SOUNDS: { id: SoundType; name: string; url: string }[] = [
  { id: 'none', name: 'None', url: '' },
  { id: 'swoosh', name: '💨 Swoosh', url: 'https://remotionlambda-useast1-2eywyfynbf.s3.us-east-1.amazonaws.com/assets/swoosh.wav' },
  { id: 'pop', name: '🎈 Pop', url: 'https://remotionlambda-useast1-2eywyfynbf.s3.us-east-1.amazonaws.com/assets/swoosh.wav' }, // TODO: upload pop.mp3
  { id: 'whoosh', name: '🌪️ Whoosh', url: 'https://remotionlambda-useast1-2eywyfynbf.s3.us-east-1.amazonaws.com/assets/swoosh.wav' }, // TODO: upload whoosh.mp3
  { id: 'click', name: '🖱️ Click', url: 'https://remotionlambda-useast1-2eywyfynbf.s3.us-east-1.amazonaws.com/assets/swoosh.wav' }, // TODO: upload click.mp3
  { id: 'rise', name: '📈 Rise', url: 'https://remotionlambda-useast1-2eywyfynbf.s3.us-east-1.amazonaws.com/assets/swoosh.wav' }, // TODO: upload rise.mp3
];

export interface Scene {
  id: number;
  type: SceneType;
  start: number;
  end: number;
  transcript: string;
  beat: BeatType;
  broll: BrollConfig | null;
  overlayTemplate?: OverlayTemplate;  // legacy — still used for built-in template ID
  overlay?: OverlayConfig;  // new — embedded overlay config from S3 library
  approved?: boolean;
  notes?: string;  // User feedback for refinement round
  effect?: EffectType;
  sound?: SoundType;
  // Caption options
  captionStyle?: CaptionStyle;
  captionPosition?: CaptionPosition;
  // Per-scene caption overrides
  captionPreset?: string; // 'default' or preset ID
  captionPositionOverride?: CaptionPositionNum; // 1-8 position override
  // PIP position (for CIRCLE_PIP)
  pipPosition?: PipPosition;
}

export interface Brand {
  colors: string[];
  captionStyle: 'minimal-bottom' | 'word-highlight' | 'none';
  imageStyle: string;
}

export interface Project {
  name: string;
  created?: string;
  brand?: Brand;
  source?: {
    avatarUrl?: string;
    transcriptPath?: string;
  };
  avatarVertical?: string;
  avatarLandscape?: string;
  scenes: Scene[];
}

// Built-in overlays used as fallback when S3 library is unavailable
export const BUILTIN_OVERLAYS: OverlayLibraryEntry[] = [
  {
    id: 'phone',
    name: 'Phone in Hand',
    pngUrl: 'https://remotionlambda-useast1-2eywyfynbf.s3.us-east-1.amazonaws.com/overlays/phone/overlay.png',
    cutout: { left: 34.0, top: 21.9, width: 40.3, height: 50.9 },
    objectPosition: 'center 30%',
  },
  {
    id: 'laptop',
    name: 'Laptop Boardroom',
    pngUrl: 'https://remotionlambda-useast1-2eywyfynbf.s3.us-east-1.amazonaws.com/overlays/laptop/overlay.png',
    cutout: { left: 23.8, top: 33.0, width: 52.5, height: 20.5 },
    objectPosition: 'center 25%',
  },
  {
    id: 'stadium',
    name: 'Stadium Jumbotron',
    pngUrl: 'https://remotionlambda-useast1-2eywyfynbf.s3.us-east-1.amazonaws.com/overlays/stadium/overlay.png',
    cutout: { left: 19.6, top: 39.3, width: 60.2, height: 20.8 },
    objectPosition: 'center 25%',
  },
  {
    id: 'iphone15',
    name: 'iPhone 15 Pro',
    pngUrl: 'https://remotionlambda-useast1-2eywyfynbf.s3.us-east-1.amazonaws.com/overlays/iphone15/overlay.png',
    cutout: { left: 18.8, top: 16.7, width: 62.3, height: 74.9 },
    objectPosition: 'center 30%',
  },
  {
    id: 'tv',
    name: 'TV Living Room',
    pngUrl: 'https://remotionlambda-useast1-2eywyfynbf.s3.us-east-1.amazonaws.com/overlays/overlay-tv-room.png',
    cutout: { left: 6.7, top: 24.9, width: 87.1, height: 27.5 },
    objectPosition: 'center center',
  },
];

// Legacy compat — kept so existing imports don't break
export const OVERLAY_TEMPLATES: Record<string, { label: string; icon: string; overlay: string }> = Object.fromEntries(
  BUILTIN_OVERLAYS.map(o => [o.id, { label: o.name, icon: '📺', overlay: o.pngUrl }])
);

export const SCENE_TYPE_CONFIG: Record<SceneType, {
  label: string;
  icon: string;
  description: string;
  brollRequired: boolean;
  brollShape: 'square' | 'vertical' | null;
}> = {
  SPEAKER_ONLY: {
    label: 'Speaker Only',
    icon: '🎤',
    description: 'Full-frame avatar, no b-roll',
    brollRequired: false,
    brollShape: null,
  },
  SPLIT_SCREEN: {
    label: 'Split Screen',
    icon: '🖼️',
    description: 'Avatar bottom, b-roll top',
    brollRequired: true,
    brollShape: 'square',
  },
  CIRCLE_PIP: {
    label: 'Circle PIP',
    icon: '⭕',
    description: 'Small avatar circle over full b-roll',
    brollRequired: true,
    brollShape: 'vertical',
  },
  FULL_BROLL: {
    label: 'Full B-Roll',
    icon: '🎬',
    description: 'B-roll fills screen, avatar hidden',
    brollRequired: true,
    brollShape: 'vertical',
  },
  FULL_BLEED_BROLL: {
    label: 'Full Bleed',
    icon: '🌄',
    description: 'B-roll fills screen, audio continues',
    brollRequired: true,
    brollShape: 'vertical',
  },
  OVERLAY: {
    label: 'Overlay',
    icon: '📺',
    description: 'Avatar on screen (phone, laptop, etc.)',
    brollRequired: false,
    brollShape: null,
  },
};

// B-roll source options
export const BROLL_SOURCES: { id: BrollSourceType; name: string; icon: string; description: string; example: string }[] = [
  { id: 'flux', name: 'Flux AI', icon: '🎨', description: 'AI-generated cinematic imagery', example: '"modern office, soft lighting, tech startup"' },
  { id: 'lora', name: 'LoRA', icon: '✨', description: 'Your custom trained style', example: 'Product shots, brand aesthetic' },
  { id: 'brave', name: 'Brave Search', icon: '🔍', description: 'Real photos from the web', example: 'Logos, screenshots, real places' },
  { id: 'pexels', name: 'Pexels', icon: '📷', description: 'Free HD stock photos & videos', example: 'Office, technology, people, nature' },
  { id: 'infographic', name: 'Infographic', icon: '📊', description: 'Animated slide-in graphic', example: 'Data viz, stats, charts' },
  { id: 'component', name: 'Component', icon: '🧩', description: 'Animated React component', example: 'Metric cards, donut charts' },
  { id: 'upload', name: 'Upload', icon: '📁', description: 'Your own images', example: 'Screenshots, custom graphics' },
  { id: 'library', name: 'Library', icon: '📚', description: 'Pre-approved assets', example: 'Curated b-roll, infographics' },
  { id: 'lottie', name: 'Lottie', icon: '🎬', description: 'Animated JSON from LottieFiles', example: 'Motion graphics, icons, transitions' },
];

// Infographic options
export const INFOGRAPHIC_DIRECTIONS: { id: InfographicDirection; name: string }[] = [
  { id: 'bottom', name: '⬆️ Slide Up' },
  { id: 'top', name: '⬇️ Slide Down' },
  { id: 'left', name: '➡️ Slide Right' },
  { id: 'right', name: '⬅️ Slide Left' },
];

export const INFOGRAPHIC_BG_STYLES: { id: InfographicBgStyle; name: string; description: string }[] = [
  { id: 'static', name: 'Static', description: 'Clean dark background' },
  { id: 'bokeh', name: 'Bokeh', description: 'Floating teal glows' },
  { id: 'pulse', name: 'Pulse', description: 'Breathing center glow' },
];

// Component library
export const COMPONENT_TYPES: { id: ComponentType; name: string; icon: string; description: string }[] = [
  { id: 'winner-card', name: 'Winner Card', icon: '👑', description: 'Crown + celebration animation' },
  { id: 'template-dashboard', name: 'Dashboard', icon: '📊', description: 'KPI cards — pass data via componentProps' },
];

// Caption styles
export const CAPTION_STYLES: { id: CaptionStyle; name: string; description: string }[] = [
  { id: 'teal-pill', name: 'Teal Pill', description: 'Rounded teal background' },
  { id: 'word-highlight', name: 'Word Highlight', description: '2 words at a time' },
  { id: 'minimal', name: 'Minimal', description: 'Simple white text' },
  { id: 'none', name: 'None', description: 'No captions' },
];

export const CAPTION_POSITIONS: { id: CaptionPosition; name: string; offset: number }[] = [
  { id: 'bottom', name: 'Bottom', offset: 120 },
  { id: 'lower-third', name: 'Lower Third', offset: 320 },
  { id: 'middle', name: 'Middle', offset: 600 },
];

// PIP position grid (3x3)
export const PIP_POSITIONS: { id: PipPosition; label: string; row: number; col: number; coords: { bottom?: number; top?: number; left?: number; right?: number } }[] = [
  { id: 'top-left', label: 'TL', row: 0, col: 0, coords: { top: 120, left: 40 } },
  { id: 'top-center', label: 'TC', row: 0, col: 1, coords: { top: 120, left: 400 } },
  { id: 'top-right', label: 'TR', row: 0, col: 2, coords: { top: 120, right: 40 } },
  { id: 'middle-left', label: 'ML', row: 1, col: 0, coords: { top: 820, left: 40 } },
  { id: 'middle-center', label: 'MC', row: 1, col: 1, coords: { top: 820, left: 400 } },
  { id: 'middle-right', label: 'MR', row: 1, col: 2, coords: { top: 820, right: 40 } },
  { id: 'bottom-left', label: 'BL', row: 2, col: 0, coords: { bottom: 320, left: 40 } },
  { id: 'bottom-center', label: 'BC', row: 2, col: 1, coords: { bottom: 320, left: 400 } },
  { id: 'bottom-right', label: 'BR', row: 2, col: 2, coords: { bottom: 320, right: 40 } },
];

export const BEAT_LABELS: Record<BeatType, { label: string; color: string }> = {
  HOOK: { label: 'Hook', color: 'bg-red-500' },
  SETUP: { label: 'Setup', color: 'bg-blue-500' },
  REVEAL: { label: 'Reveal', color: 'bg-purple-500' },
  ESCALATE: { label: 'Escalate', color: 'bg-orange-500' },
  EVIDENCE: { label: 'Evidence', color: 'bg-green-500' },
  PATTERN_INTERRUPT: { label: 'Pattern Interrupt', color: 'bg-pink-500' },
  DARK_TURN: { label: 'Dark Turn', color: 'bg-gray-600' },
  THESIS: { label: 'Thesis', color: 'bg-yellow-500' },
  MIC_DROP: { label: 'Mic Drop', color: 'bg-indigo-500' },
};
